import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * GET `path` from the API and track its state:
 *   status: "idle" (path is null) | "loading" | "ok" | "notfound" | "error"
 * The request is cancelled when `path` changes or the component unmounts, so a
 * late response never overwrites newer data and a cancelled request never
 * surfaces as an unhandled error. `reload()` refetches and keeps the current
 * data on screen until the new response arrives.
 */
export function useFetch(path) {
  const [state, setState] = useState({ status: path ? "loading" : "idle", data: null, path });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) { setState({ status: "idle", data: null, path }); return undefined; }
    const ctrl = new AbortController();
    // A new path starts empty; a reload of the same path keeps what is shown.
    setState((s) => ({ status: "loading", data: s.path === path ? s.data : null, path }));
    api.get(path, { signal: ctrl.signal })
      .then((r) => setState({ status: "ok", data: r.data, path }))
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setState({ status: e?.response?.status === 404 ? "notfound" : "error", data: null, path });
      });
    return () => ctrl.abort();
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { status: state.status, data: state.data, reload };
}
